// src/components/ContactUsWebToLead.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/ApplicationForm.css";

/**
 * ContactUsWebToLead — SPA-safe Web-to-Lead + reCAPTCHA v2 checkbox
 *
 * WHY YOURS DIDN'T WORK:
 * In a React SPA, recaptcha/api.js may already be loaded before this route mounts.
 * In that case, Google's auto-render often does NOT run again -> widget never initializes
 * -> g-recaptcha-response stays empty -> Salesforce rejects silently.
 *
 * FIX:
 * We explicitly render the widget via grecaptcha.render() once grecaptcha exists.
 */

const ORG_ID = "00D7z00000PZ0ef";
const ACTION_URL =
  "https://test.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00D7z00000PZ0ef";
const RET_URL = "http://causeandimpact.org/contact-submitted";

// IMPORTANT: Use the specific keyname you said was created for Contact Us
const CAPTCHA_SETTINGS =
  '{"keyname":"CauseImpact_ContactUsToLead","fallback":"true","orgId":"00D7z00000PZ0ef","ts":""}';

const RECAPTCHA_SITEKEY = "6LdKEpwqAAAAALaRs1U34EXHuG_eDnk5fFXuuPL9";

// Custom W2L field IDs from your generated Contact Us form
const COMMENTS_W2L = "00N7z00000nHcEL";
const TOPIC_W2L = "00N7z00000nHbwb";

const TOPIC_OPTIONS = [
  { value: "", label: "--None--" },
  { value: "Billing / Invoicing", label: "Billing / Invoicing" },
  { value: "Fiscal Sponsorship Inquiry", label: "Fiscal Sponsorship Inquiry" },
  { value: "General Question", label: "General Question" },
  { value: "Other", label: "Other" },
  { value: "Partnership / Vendor", label: "Partnership / Vendor" },
  { value: "Press / Media", label: "Press / Media" },
  { value: "Support / Issue", label: "Support / Issue" },
];

const trim = (v) => (v ?? "").toString().trim();

export default function ContactUsWebToLead() {
  const [submitting, setSubmitting] = useState(false);
  const [clientError, setClientError] = useState("");

  const formRef = useRef(null);
  const submitLockRef = useRef(false);

  // Where we will force-render the captcha widget
  const captchaMountRef = useRef(null);
  const widgetIdRef = useRef(null);

  const debugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    const p = new URLSearchParams(window.location.search);
    return p.get("debug") === "1";
  }, []);

  const debugEmail = useMemo(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams(window.location.search);
    // allow ?debugEmail=you@domain.com
    return p.get("debugEmail") || "Leightonjacobus@causeandimpact.org";
  }, []);

  useEffect(() => {
    // 1) Ensure reCAPTCHA script exists
    const ensureScript = () =>
      new Promise((resolve) => {
        const existing = document.querySelector(
          'script[src="https://www.google.com/recaptcha/api.js"]'
        );
        if (existing) return resolve();

        const s = document.createElement("script");
        s.src = "https://www.google.com/recaptcha/api.js";
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });

    // 2) Wait for window.grecaptcha to exist, then render explicitly
    const renderCaptcha = async () => {
      await ensureScript();

      const start = Date.now();
      const timeoutMs = 8000;

      const poll = () =>
        new Promise((resolve, reject) => {
          const tick = () => {
            if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
              return resolve();
            }
            if (Date.now() - start > timeoutMs) {
              return reject(new Error("grecaptcha did not load in time"));
            }
            setTimeout(tick, 100);
          };
          tick();
        });

      try {
        await poll();

        // If already rendered from previous mount, reset
        if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
          window.grecaptcha.reset(widgetIdRef.current);
          return;
        }

        // Force render into our mount node
        if (captchaMountRef.current && window.grecaptcha?.render) {
          widgetIdRef.current = window.grecaptcha.render(captchaMountRef.current, {
            sitekey: RECAPTCHA_SITEKEY,
          });
        }
      } catch (e) {
        // If captcha can't load, Salesforce fallback:true may still allow post,
        // but typically SF requires captcha. We'll show a clear UX error instead.
        setClientError(
          "Captcha failed to load. Please refresh the page and try again."
        );
      }
    };

    renderCaptcha();

    // 3) Mirror Salesforce timestamp updater for captcha_settings
    const interval = setInterval(() => {
      const response = document.getElementById("g-recaptcha-response");
      const captchaSettingsInput = document.getElementsByName("captcha_settings")?.[0];
      if (!captchaSettingsInput) return;

      try {
        if (!response || response.value.trim() === "") {
          const elems = JSON.parse(captchaSettingsInput.value);
          elems.ts = JSON.stringify(new Date().getTime());
          captchaSettingsInput.value = JSON.stringify(elems);
        }
      } catch {
        // no-op
      }
    }, 500);

    return () => {
      clearInterval(interval);
      // optional: reset on unmount (prevents stale tokens)
      try {
        if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
          window.grecaptcha.reset(widgetIdRef.current);
        }
      } catch {
        // ignore
      }
    };
  }, []);

  const handleSubmit = (e) => {
    setClientError("");

    if (submitLockRef.current) {
      e.preventDefault();
      return;
    }

    const form = e.currentTarget;

    // Required (Lead requires LastName + Company)
    const lastName = trim(form.querySelector('input[name="last_name"]')?.value);
    const company = trim(form.querySelector('input[name="company"]')?.value);

    // UX-required for Contact Us
    const topic = trim(form.querySelector(`select[name="${TOPIC_W2L}"]`)?.value);
    const comments = trim(form.querySelector(`textarea[name="${COMMENTS_W2L}"]`)?.value);

    if (!lastName) {
      e.preventDefault();
      setClientError("Please enter your Last Name.");
      return;
    }
    if (!company) {
      e.preventDefault();
      setClientError("Please enter your Cause / Project Name.");
      return;
    }
    if (!topic) {
      e.preventDefault();
      setClientError("Please select a Topic.");
      return;
    }
    if (!comments) {
      e.preventDefault();
      setClientError("Please enter a Message.");
      return;
    }

    // Ensure reCAPTCHA completed (Salesforce will reject without it)
    const recaptchaResponse = trim(document.getElementById("g-recaptcha-response")?.value);
    if (!recaptchaResponse) {
      e.preventDefault();
      setClientError("Please complete the reCAPTCHA.");
      return;
    }

    // Normalize email before submit (optional)
    const emailInput = form.querySelector('input[name="email"]');
    if (emailInput?.value) emailInput.value = trim(emailInput.value).toLowerCase();

    // Force-update ts right before submit
    try {
      const captchaSettingsInput = form.querySelector('input[name="captcha_settings"]');
      if (captchaSettingsInput) {
        const elems = JSON.parse(captchaSettingsInput.value);
        elems.ts = JSON.stringify(new Date().getTime());
        captchaSettingsInput.value = JSON.stringify(elems);
      }
    } catch {
      // ignore
    }

    submitLockRef.current = true;
    setSubmitting(true);
  };

  return (
    <div className="application-wrapper">
      <form
        ref={formRef}
        className="application-form"
        action={ACTION_URL}
        method="POST"
        onSubmit={handleSubmit}
      >
        {/* REQUIRED hidden fields */}
        <input type="hidden" name="captcha_settings" value={CAPTCHA_SETTINGS} />
        <input type="hidden" name="oid" value={ORG_ID} />
        <input type="hidden" name="retURL" value={RET_URL} />
        <input type="hidden" name="lead_source" value="Contact Us" />

        {/* OPTIONAL: turn on Salesforce Web-to-Lead debug email via ?debug=1 */}
        {debugEnabled ? (
          <>
            <input type="hidden" name="debug" value="1" />
            <input type="hidden" name="debugEmail" value={debugEmail} />
          </>
        ) : null}

        <h2>Contact Us</h2>
        <p>Send us a message and we’ll follow up shortly.</p>

        <div className="form-section">
          <div className="form-section-header">
            <h3>Your Info</h3>
          </div>

          <div className="form-row form-row-2">
            <label htmlFor="first_name">
              First Name (optional)
              <input id="first_name" name="first_name" maxLength="40" type="text" />
            </label>

            <label htmlFor="last_name">
              Last Name (required)
              <input id="last_name" name="last_name" maxLength="80" type="text" required />
            </label>
          </div>

          <div className="form-row form-row-2">
            <label htmlFor="email">
              Email (optional)
              <input id="email" name="email" maxLength="80" type="email" />
            </label>

            <label htmlFor="mobile">
              Mobile (optional)
              {/* IMPORTANT: Web-to-Lead expects name="mobile" for Lead Mobile */}
              <input id="mobile" name="mobile" maxLength="40" type="tel" />
            </label>
          </div>

          <div className="form-row">
            <label htmlFor="company">
              Cause / Project Name (required)
              <input id="company" name="company" maxLength="40" type="text" required />
            </label>
          </div>

          <div className="form-row">
            <label htmlFor={TOPIC_W2L}>
              Topic (required)
              <select id={TOPIC_W2L} name={TOPIC_W2L} defaultValue="" required>
                {TOPIC_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label htmlFor={COMMENTS_W2L}>
              Contact Us Comments (required)
              <textarea
                id={COMMENTS_W2L}
                name={COMMENTS_W2L}
                rows="5"
                wrap="soft"
                required
                placeholder="Type your message here..."
              />
            </label>
          </div>

          {clientError ? <p style={{ color: "#ef4444", marginTop: 10 }}>{clientError}</p> : null}
        </div>

        <div className="form-section">
          <div className="captcha-container">
            {/* IMPORTANT:
                We do NOT rely on <div class="g-recaptcha"> auto-render.
                We explicitly render into this mount ref in useEffect. */}
            <div ref={captchaMountRef} />
          </div>

          <div className="form-section submit-section">
            <div className="submit-container">
              <button type="submit" className="submit-button" disabled={submitting}>
                <span>{submitting ? "Submitting..." : "Send Message"}</span>
              </button>
            </div>
          </div>

          {debugEnabled ? (
            <p style={{ color: "#94a3b8", fontSize: "0.9rem", marginTop: 10 }}>
              Debug mode ON — Salesforce will email errors to: {debugEmail}
            </p>
          ) : (
            <p style={{ color: "#94a3b8", fontSize: "0.9rem", marginTop: 10 }}>
              Tip: append <code>?debug=1</code> to this page URL to get Salesforce debug emails.
            </p>
          )}
        </div>
      </form>
    </div>
  );
}